import { z } from 'zod'
import { ALL_CONTENT_TYPES, MAX_RECIPIENTS_PER_CHANNEL } from '@/lib/types'

// Matches CreateClientInput on the backend
export const createClientSchema = z.object({
  brandName: z.string().min(1, 'Brand name is required').max(100),
  brandLocation: z.string().optional(),
  brandSpecialization: z.string().optional(),
  industry: z.string().optional(),
  retrieverProvider: z.string().optional(),
  websiteUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  modelProvider: z.string().optional(),
  modelId: z.string().optional(),
  providerApiKey: z.string().optional(),
  agentName: z.string().optional(),
  selectedContentTypes: z.array(z.string()).min(1, 'Select at least one content type').optional(),
  autoRun: z.boolean().optional(),
  autoPublish: z.boolean().optional(),
  budgetLimitDailyUsd: z.number().min(0).optional(),
})

// Matches UpdateClientInput on the backend
export const updateClientSchema = z.object({
  brandName: z.string().min(1).max(100).optional(),
  brandLocation: z.string().optional(),
  brandSpecialization: z.string().optional(),
  industry: z.string().optional(),
  websiteUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  modelProvider: z.string().optional(),
  modelId: z.string().optional(),
  providerApiKey: z.string().optional(),
  agentName: z.string().optional(),
  selectedContentTypes: z.array(z.string()).optional(),
  seoKeywords: z.string().optional(),
  compliancePrompt: z.string().optional(),
  autoRun: z.boolean().optional(),
  autoPublish: z.boolean().optional(),
  budgetLimitDailyUsd: z.number().min(0).optional(),
  notificationEmail: z.string().optional(),
  notificationPhone: z.string().optional(),
})

// Matches GenerateInput on the backend
export const generateChatSchema = z.object({
  clientId: z.string().min(1),
  message: z.string().min(1, 'Message is required'),
  contentType: z.string().min(1, 'Content type is required'),
})

// For updating account display info
export const updateAccountSchema = z.object({
  displayName: z.string().optional(),
  photoUrl: z.url().optional().or(z.literal('')),
})

// For creating/updating alerts
export const createAlertSchema = z.object({
  name: z.string().min(1, 'Alert name is required'),
  metric: z.string().min(1, 'Metric is required'),
  threshold: z.number().min(0),
  condition: z.enum(['above', 'below']),
  channel: z.string().min(1),
  address: z.string().min(1),
})

// For creating templates
export const createTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  contentType: z.string().min(1, 'Content type is required'),
  content: z.string().min(1, 'Content is required'),
  tags: z.array(z.string()).optional(),
})

export type CreateClientFormData = z.infer<typeof createClientSchema>
export type UpdateClientFormData = z.infer<typeof updateClientSchema>
export type GenerateChatFormData = z.infer<typeof generateChatSchema>
export type UpdateAccountFormData = z.infer<typeof updateAccountSchema>
export type CreateAlertFormData = z.infer<typeof createAlertSchema>
export type CreateTemplateFormData = z.infer<typeof createTemplateSchema>

// For login form
export const loginSchema = z.object({
  email: z.email('Must be a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})
export type LoginInput = z.infer<typeof loginSchema>

// For registration form
export const registerSchema = z.object({
  name: z.string().min(1, 'Full name is required'),
  email: z.email('Must be a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Password must be at least 6 characters'),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
})
export type RegisterInput = z.infer<typeof registerSchema>

// For SendGrid credentials
export const sendgridCredentialsSchema = z.object({
  api_key: z.string().min(1, 'API key is required'),
  from_email: z.string().email('Must be a valid email'),
  from_name: z.string().optional(),
})
export type SendgridCredentialsFormData = z.infer<typeof sendgridCredentialsSchema>

export const modelSettingsSchema = z.object({
  modelProvider: z.string().min(1, 'Provider is required'),
  modelId: z.string().min(1, 'Model is required'),
  providerApiKey: z.string().optional(),
})

export type ModelSettingsFormData = z.infer<typeof modelSettingsSchema>

// Wizard Step 1: Business Info
export const wizardStep1Schema = z.object({
  brandName: z.string().min(1, 'Brand name is required').max(100),
  brandSpecialization: z.string().optional(),
  industry: z.string().optional(),
  websiteUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  brandLocation: z.string().optional(),
  phone: z.string().optional(),
  serviceArea: z.string().optional(),
})

// Wizard Step 2: Online Presence
export const wizardStep2Schema = z.object({
  facebookUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  linkedinUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  twitterUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
  ctaUrl: z.url('Must be a valid URL').optional().or(z.literal('')),
})

// Wizard Step 3: Services & Insurance
export const wizardStep3Schema = z.object({
  businessDescription: z.string().optional(),
  productCategories: z.array(z.string()).optional(),
  servicesOffered: z.array(z.string()).optional(),
  deliveryTimeline: z.string().optional(),
  acceptedInsurance: z.array(z.string()).optional(),
  ehrIntegrations: z.array(z.string()).optional(),
})

// Wizard Step 4: Campaign Setup
export const wizardStep4Schema = z.object({
  campaignGoal: z.string().optional(),
  selectedContentTypes: z.array(z.string()).min(1, 'Select at least one content type').optional(),
  budgetLimitDailyUsd: z.number().min(0, 'Must be 0 or greater').optional(),
  includeAddress: z.boolean().optional(),
  includePhone: z.boolean().optional(),
  includeEmail: z.boolean().optional(),
  includeWebsite: z.boolean().optional(),
  includePricing: z.boolean().optional(),
  includeStaffNames: z.boolean().optional(),
})

// Wizard Step 5: Auto-generation Schedule
// Note: cross-field validation (e.g. "enabled requires time_utc") is applied in
// the wizard's combined schema via `.superRefine`, not here, so this object
// remains mergeable with the other wizard step schemas.
export const wizardStep5Schema = z.object({
  scheduleEnabled: z.boolean().optional(),
  scheduleContentTypes: z.array(z.enum(ALL_CONTENT_TYPES)).optional(),
  scheduleCadence: z.enum(['daily', 'weekly', 'custom_cron']).optional(),
  scheduleTimeUtc: z.string().optional(),
  scheduleDaysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  scheduleCronExpr: z.string().optional(),
  scheduleTimezone: z.string().optional(),
  scheduleNotifyOnQueue: z.boolean().optional(),
})

export type WizardStep1FormData = z.infer<typeof wizardStep1Schema>
export type WizardStep2FormData = z.infer<typeof wizardStep2Schema>
export type WizardStep3FormData = z.infer<typeof wizardStep3Schema>
export type WizardStep4FormData = z.infer<typeof wizardStep4Schema>
export type WizardStep5FormData = z.infer<typeof wizardStep5Schema>

// ─── Notification Recipients ────────────────────────────────────────────────

const e164 = z.string().regex(/^\+[1-9]\d{6,14}$/, 'Must be a valid E.164 phone (e.g. +14155551234)')

export const notificationRecipientSchema = z.object({
  id: z.string().min(1),
  channel: z.enum(['whatsapp', 'telegram']),
  label: z.string().max(40).optional(),
  phone: z.string().optional(),
  chat_id: z.string().optional(),
  verified_at: z.string().optional(),
  added_at: z.string(),
  persisted: z.boolean().optional(),
}).superRefine((r, ctx) => {
  if (r.channel === 'whatsapp') {
    const parsed = e164.safeParse(r.phone)
    if (!parsed.success) {
      ctx.addIssue({ code: 'custom', path: ['phone'], message: parsed.error.issues[0]?.message ?? 'Invalid phone' })
    }
  } else if (r.channel === 'telegram') {
    if (!r.chat_id || r.chat_id.trim().length === 0) {
      ctx.addIssue({ code: 'custom', path: ['chat_id'], message: 'Telegram chat ID is required' })
    }
  }
})

export const notificationRecipientsSchema = z.array(notificationRecipientSchema).superRefine((arr, ctx) => {
  const counts = { whatsapp: 0, telegram: 0 }
  arr.forEach((r, i) => {
    counts[r.channel]++
    if (counts[r.channel] > MAX_RECIPIENTS_PER_CHANNEL) {
      ctx.addIssue({
        code: 'custom',
        path: [i],
        message: `Max ${MAX_RECIPIENTS_PER_CHANNEL} recipients per channel`,
      })
    }
  })
})

export type NotificationRecipientFormData = z.infer<typeof notificationRecipientSchema>

// ─── Schedule Config ────────────────────────────────────────────────────────

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Must be HH:MM (24h UTC)')

export const schedulingConfigSchema = z.object({
  auto_generate: z.object({
    enabled: z.boolean(),
    content_types: z.array(z.enum(ALL_CONTENT_TYPES)).min(1, 'Pick at least one content type'),
    cadence: z.enum(['daily', 'weekly', 'custom_cron']),
    time_utc: hhmm,
    days_of_week: z.array(z.number().int().min(0).max(6)).optional(),
    cron_expr: z.string().optional(),
    timezone: z.string().optional(),
  }).superRefine((g, ctx) => {
    if (g.cadence === 'weekly' && (!g.days_of_week || g.days_of_week.length === 0)) {
      ctx.addIssue({ code: 'custom', path: ['days_of_week'], message: 'Pick at least one day' })
    }
    if (g.cadence === 'custom_cron' && (!g.cron_expr || g.cron_expr.trim().length === 0)) {
      ctx.addIssue({ code: 'custom', path: ['cron_expr'], message: 'Cron expression is required' })
    }
  }).optional(),
  notify_on_queue: z.boolean().optional(),
})

export type SchedulingConfigFormData = z.infer<typeof schedulingConfigSchema>

// ─── Campaign: enter missing emails ─────────────────────────────────────────

export const enterMissingEmailsRowSchema = z.object({
  id: z.string(),
  email: z.union([z.email('Enter a valid email address'), z.literal('')]),
})

export const enterMissingEmailsSchema = z.object({
  rows: z.array(enterMissingEmailsRowSchema),
})

export type EnterMissingEmailsFormData = z.infer<typeof enterMissingEmailsSchema>

// ─── Campaign: add provider manually ────────────────────────────────────────

export const addManualProviderSchema = z.object({
  name: z.string().min(1, 'Provider name is required'),
  email: z.email('Enter a valid email address'),
  npi: z.string().optional(),
})

export type AddManualProviderFormData = z.infer<typeof addManualProviderSchema>
